import Modal from "react-bootstrap/Modal";
import { Button, Spinner } from "react-bootstrap";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import CustomPasswordInput from "./CustomPasswordInput";

/**
 * @typedef {Object} ChangePasswordModalProps
 * @property {boolean} showModal - Controls the visibility of the modal
 * @property {() => void} handleClose - Function to close the modal
 * @property {boolean} loading - Indicates if the password change operation is in progress
 * @property {(values: { currentPassword: string, newPassword: string }) => Promise<boolean>} handleChangePassword - Async function to handle password change
 * @property {string} [error] - Error message from the API
 * @property {boolean} [success] - Indicates if the password change was successful
 */

/**
 * @param {ChangePasswordModalProps} props
 */
const ChangePasswordModal = ({
  showModal,
  handleClose,
  handleChangePassword,
  loading = false,
  error = "",
  success = false,
}) => {
  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .required("New password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character",
      )
      .notOneOf(
        [Yup.ref("currentPassword")],
        "New password must be different from current password",
      ),
    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("newPassword")], "Passwords must match"),
  });

  const onSubmit = async (values, { resetForm }) => {
    const result = await handleChangePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });

    if (result) {
      resetForm();
    }
  };

  // Icon based on state
  const getIcon = () => {
    if (success) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-check-circle-fill text-success fs-2"></i>
        </div>
      );
    }
    if (error) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-x-circle-fill text-danger fs-2"></i>
        </div>
      );
    }
    return (
      <div></div>

    );
  };

  const getTitle = () => {
    if (success) return "Password Changed";
    if (error) return "Update Failed";
    return "Change Password";
  };

  // Success state
  if (success) {
    return (
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        className="custom-modal"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body className="p-5">
          <div className="text-center mb-3">{getIcon()}</div>
          <div className="text-center mb-2">
            <h5 className="fw-bold fs-4">{getTitle()}</h5>
          </div>
          <p className="text-center text-muted mb-4">
            Your password has been updated successfully.
          </p>
          <div className="d-flex justify-content-center">
            <Button className="custom-primary-button" onClick={handleClose}>
              Continue
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      className="custom-modal"
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => (
          <FormikForm>
            <Modal.Body className="p-5">
              {/* <div className="text-center mb-3">{getIcon()}</div> */}
              <div className="text-center mb-2">
                <h5 className="fw-bold fs-4">{getTitle()}</h5>
              </div>

              {/* Error Message */}
              {error && (
                <p className="text-center text-danger mb-4">{error}</p>
              )}

              {/* Current Password */}
              <div className="mb-3">
                <CustomPasswordInput
                  label="Current Password *"
                  name="currentPassword"
                  placeholder="Enter current password"
                  layout="stacked"
                  disabled={loading}
                />
              </div>

              {/* New Password */}
              <div className="mb-3">
                <CustomPasswordInput
                  label="New Password *"
                  name="newPassword"
                  placeholder="Enter new password"
                  layout="stacked"
                  disabled={loading}
                />
              </div>

              {/* Confirm Password */}
              <div className="mb-4">
                <CustomPasswordInput
                  label="Confirm New Password *"
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  layout="stacked"
                  disabled={loading}
                />
              </div>

              {/* Buttons */}
              <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                <Button
                  type="button"
                  className="custom-cancel-button"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={loading || isSubmitting}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </Button>
              </div>
            </Modal.Body>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export default ChangePasswordModal;
